import { useCallback, useEffect, useRef, useState } from "react";
import { useExtensionRuntimeTransport } from "./ExtensionRuntimeProvider";
import type { McpTransport } from "./transport";

export interface UseMcpResourceOptions
{
    /**
     * Override the transport resolved from {@link ExtensionRuntimeProvider}.
     * Primarily intended for tests and advanced multi-host scenarios.
     */
    transport?: McpTransport;
}

export interface UseMcpResourceResult<T>
{
    data: T | undefined;
    error: Error | undefined;
    loading: boolean;
    /** Re-run the fetch against the current URI. Stable across renders. */
    refetch: () => void;
}

/**
 * Subscribe to an MCP resource by URI.
 *
 * The hook fetches via the resolved {@link McpTransport} on mount and whenever
 * the URI changes. Each fetch is cancellable: when the component unmounts or
 * the URI changes, the in-flight call is aborted via an {@link AbortController}
 * so stale responses cannot overwrite later state.
 *
 * `refetch` re-runs the fetch against the latest URI. The returned callback is
 * stable across renders so it is safe to include in `useEffect` dependency
 * arrays.
 */
export function useMcpResource<T>(
    uri: string,
    opts?: UseMcpResourceOptions,
): UseMcpResourceResult<T>
{
    const transport = useExtensionRuntimeTransport(opts?.transport);

    const [data, setData] = useState<T | undefined>(undefined);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);

    // Bump this counter to force a re-fetch from `refetch()`.
    const [refetchTick, setRefetchTick] = useState(0);

    // Track the active controller so `refetch` and unmount can abort it.
    const controllerRef = useRef<AbortController | null>(null);

    useEffect(() =>
    {
        const controller = new AbortController();
        controllerRef.current?.abort();
        controllerRef.current = controller;

        let cancelled = false;
        setLoading(true);
        setError(undefined);

        transport
            .getResource<T>(uri, controller.signal)
            .then((result) =>
            {
                if (cancelled || controller.signal.aborted)
                {
                    return;
                }
                setData(result.data);
                setLoading(false);
            })
            .catch((err: unknown) =>
            {
                if (cancelled || controller.signal.aborted)
                {
                    return;
                }
                setError(err instanceof Error ? err : new Error(String(err)));
                setLoading(false);
            });

        return () =>
        {
            cancelled = true;
            controller.abort();
        };
    }, [uri, transport, refetchTick]);

    const refetch = useCallback(() =>
    {
        setRefetchTick((tick) => tick + 1);
    }, []);

    return { data, error, loading, refetch };
}
